import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Modal,
  Button,
  Form,
  Col,
  InputGroup,
  Spinner
} from '@themesberg/react-bootstrap';
import { X, Plus } from 'lucide-react';
import EditableDropDown from '../../components/common/EditableDropdown';
// Import your EditableDropDown component
// import EditableDropDown from './EditableDropDown';

// Simulated API call to fetch template data (for edit/clone mode)
const fetchTemplateData = async (templateId) => {
  try {
    const response = await fetch(`http://localhost:1338/internal/template/${templateId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
        // Include any needed authorization headers
      }
    });
    if (!response.ok) throw new Error('Failed to fetch template data');
    const result = await response.json();
    return result?.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

// Fetch available questions from your GET questions API
const fetchAvailableQuestions = async () => {
  try {
    const response = await fetch('http://localhost:1338/internal/questions', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
        // Include any needed authorization headers
      }
    });
    if (!response.ok) throw new Error('Failed to fetch questions');
    const result = await response.json();
    return result?.data || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

// Simulated available options for brands, products, and companies.
const fetchAvailableBrands = async () => {
  return Promise.resolve([
    { id: 'brandId1', name: 'Alpha' },
    { id: 'brandId2', name: 'Beta' }
  ]);
};

const fetchAvailableProducts = async () => {
  return Promise.resolve([
    { id: 'prodId1', name: 'Samsung Galaxy S23' },
    { id: 'prodId2', name: 'iPhone 13' }
  ]);
};

const fetchAvailableCompanies = async () => {
  return Promise.resolve([
    { id: 'companyId1', name: 'Samsung' },
    { id: 'companyId2', name: 'Apple' }
  ]);
};

// Simulated function to fetch BuyBack SKU options.
const fetchAvailableBuyBackSkus = async () => {
  return Promise.resolve([
    { id: 'sku1', name: 'Option 1' },
    { id: 'sku2', name: 'Option 2' }
  ]);
};

const CreateNewTemplate = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id'); // e.g. ?id=123
  const isCloning = searchParams.get('clone') === 'true';
  const isEditMode = Boolean(templateId) && !isCloning;
  const navigate = useNavigate();

  // --- Template Details ---
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');

  // -------------------------------------------------------------
  // Available Questions (fetched from GET API)
  const [availableQuestions, setAvailableQuestions] = useState([]);
  // Selected questions are stored as question IDs.
  const [selectedQuestions, setSelectedQuestions] = useState(['']);

  useEffect(() => {
    fetchAvailableQuestions().then(setAvailableQuestions);
  }, []);

  // Convert available questions into the expected option structure,
  // including a default option of "Assigned".
  const questionOptions = [
    { label: 'Assigned', value: 'Assigned' },
    ...availableQuestions.map((q) => ({ value: q.objectId, label: q.text }))
  ];

  const handleQuestionChange = (index, selectedOption) => {
    const value =
      selectedOption && selectedOption.length > 0
        ? selectedOption[0].value
        : '';
    const newSelected = [...selectedQuestions];
    newSelected[index] = value;
    setSelectedQuestions(newSelected);
  };

  const handleAddQuestion = () => setSelectedQuestions([...selectedQuestions, '']);
  const handleRemoveQuestion = (index) =>
    setSelectedQuestions(selectedQuestions.filter((_, i) => i !== index));

  // -------------------------------------------------------------
  // Dynamic Exchange Configurations Section
  // Each row has an "answers" property (object) plus exchangeValue, exchangeBonus, and buyBackSku.
  const [exchangeConfigRows, setExchangeConfigRows] = useState([
    { answers: {}, exchangeValue: '', exchangeBonus: '', buyBackSku: '' }
  ]);

  // Only non-empty selections are considered active.
  const activeQuestions = selectedQuestions.filter((q) => q !== '');
  // Show the configuration section only when at least 3 questions are selected.
  const showExchangeConfig = activeQuestions.length >= 3;

  // Update a key in a given exchange configuration row.
  const updateExchangeConfigRow = (rowIndex, key, value) => {
    const updatedRows = [...exchangeConfigRows];
    // Ensure row.answers exists
    const answers = updatedRows[rowIndex].answers || {};
    if (key in answers) {
      answers[key] = value;
      updatedRows[rowIndex].answers = answers;
    } else {
      updatedRows[rowIndex][key] = value;
    }
    setExchangeConfigRows(updatedRows);
  };

  // -------------------------------------------------------------
  // Additional fields: Exchange Details
  const [exchangeValue, setExchangeValue] = useState('');
  const [exchangeBonus, setExchangeBonus] = useState('');
  const [exchangeNote, setExchangeNote] = useState('');
  // -------------------------------------------------------------
  // Drop downs for creation: Brands, Products, Companies and BuyBack SKU
  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');

  const [availableBuyBackSkus, setAvailableBuyBackSkus] = useState([]);

  useEffect(() => {
    if (!isEditMode) {
      fetchAvailableBrands().then(setAvailableBrands);
      fetchAvailableProducts().then(setAvailableProducts);
      fetchAvailableCompanies().then(setAvailableCompanies);
      fetchAvailableBuyBackSkus().then(setAvailableBuyBackSkus);
    }
  }, [isEditMode]);

  // Format options so that each array uses the structure: [{ label, value }]
  const productOptions = [
    { label: 'Assigned', value: 'Assigned' },
    ...availableProducts.map((p) => ({ value: p.id, label: p.name }))
  ];
  const brandOptions = [
    { label: 'Assigned', value: 'Assigned' },
    ...availableBrands.map((b) => ({ value: b.id, label: b.name }))
  ];
  const companyOptions = [
    { label: 'Assigned', value: 'Assigned' },
    ...availableCompanies.map((c) => ({ value: c.id, label: c.name }))
  ];
  const buyBackSkuOptions = [
    { label: 'Assigned', value: 'Assigned' },
    ...availableBuyBackSkus.map((s) => ({ value: s.id, label: s.name }))
  ];

  // -------------------------------------------------------------
  // Assigned Products Section (if needed for template assignment)
  const [assignedProducts, setAssignedProducts] = useState([]);

  // -------------------------------------------------------------
  // Modal loading and confirmation
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load API data (for edit/clone mode)
  useEffect(() => {
    if (templateId) {
      setLoading(true);
      fetchTemplateData(templateId)
        .then((data) => {
          if (!data) return;
          setTemplateName(isCloning ? `${data.templateName} (Cloned)` : data.templateName);
          setDescription(data.text || '');
          setExchangeValue(data.exchangeValue || '');
          setExchangeBonus(data.exchangeBonus || '');
          setAssignedProducts(data.products || []);
          if (data.brands && data.brands.length > 0) setSelectedBrand(data.brands[0]);
          if (data.products && data.products.length > 0) setSelectedProduct(data.products[0]);
          if (data.categorys && data.categorys.length > 0) setSelectedCompany(data.categorys[0]);

          // Set selected questions:
          if (data.questionArrayIds && data.questionArrayIds.length > 0) {
            setSelectedQuestions(data.questionArrayIds);
          } else if (data.exchangeRules && data.exchangeRules.length > 0) {
            const questionKeys = Object.keys(data.exchangeRules[0]).filter(
              (key) =>
                key !== 'exchangeValue' &&
                key !== 'exchangeBonus' &&
                key !== 'buyBackVanNo' &&
                key !== 'title'
            );
            setSelectedQuestions(questionKeys);
          }
          // Normalize exchange rules ensuring each row has an answers object.
          if (data.exchangeRules && data.exchangeRules.length > 0) {
            const normalizedRules = data.exchangeRules.map((rule) => {
              if (!rule.answers) {
                const answers = Object.keys(rule).reduce((acc, key) => {
                  if (
                    key !== 'exchangeValue' &&
                    key !== 'exchangeBonus' &&
                    key !== 'buyBackVanNo' &&
                    key !== 'title'
                  ) {
                    acc[key] = rule[key] || '';
                  }
                  return acc;
                }, {});
                return {
                  answers,
                  exchangeValue: rule.exchangeValue || '',
                  exchangeBonus: rule.exchangeBonus || '',
                  buyBackSku: rule.buyBackVanNo || ''
                };
              }
              return {
                answers: rule.answers || {},
                exchangeValue: rule.exchangeValue || '',
                exchangeBonus: rule.exchangeBonus || '',
                buyBackSku: rule.buyBackVanNo || ''
              };
            });
            setExchangeConfigRows(normalizedRules);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [templateId, isCloning]);

  // -------------------------------------------------------------
  // Save Template (calls create/update API)
  const saveTemplate = async () => {
    // Validations
    if (!templateName.trim()) {
      alert('Template name cannot be empty.');
      return;
    }
    if (activeQuestions.length < 3) {
      alert('Please select at least 3 questions.');
      return;
    }
    if (showExchangeConfig) {
      for (let i = 0; i < exchangeConfigRows.length; i++) {
        if (!exchangeConfigRows[i].buyBackSku) {
          alert(`BuyBack Sku is required in exchange config row ${i + 1}.`);
          return;
        }
      }
    }

    setShowConfirm(false);

    // Build exchangeRules from exchangeConfigRows.
    const exchangeRules = exchangeConfigRows.map((row) => {
      const rule = {};
      activeQuestions.forEach((qId) => {
        rule[qId] = (row.answers && row.answers[qId]) || '';
      });
      rule.exchangeValue = row.exchangeValue;
      rule.exchangeBonus = row.exchangeBonus;
      rule.buyBackVanNo = row.buyBackSku;
      if (row.buyBackSku) {
        const sku = availableBuyBackSkus.find((s) => s.id === row.buyBackSku);
        rule.title = sku ? sku.name : '';
      } else {
        rule.title = '';
      }
      return rule;
    });

    const payload = {
      name: templateName,
      question: selectedQuestions.filter((q) => q !== ''),
      exchangeRules,
      products: selectedProduct ? [selectedProduct] : assignedProducts,
      category: selectedCompany ? [selectedCompany] : [],
      brands: selectedBrand ? [selectedBrand] : []
    };

    try {
      let response;
      if (isEditMode) {
        response = await fetch(`http://localhost:1338/internal/template/${templateId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch('http://localhost:1338/internal/template', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      if (response.ok) {
        const successMessage = isEditMode
          ? `Template ${templateId} updated successfully!`
          : isCloning
          ? 'Cloned template created successfully.'
          : 'New template created successfully.';
        alert(successMessage);
        navigate(-1);
      } else {
        const error = await response.json();
        alert(`Error saving template: ${error.message}`);
      }
    } catch (error) {
      alert(`Error saving template: ${error.message}`);
    }
  };

  // -------------------------------------------------------------
  // Overall action handlers
  const handleSave = () => setShowConfirm(true);
  const handleCancel = () => alert('Cancel pressed');
  const handleAssignNow = () => navigate('/templete/assign');

  // Layout helper styles (unchanged)
  const leftStyle = { width: '20%', fontWeight: 600, padding: '8px' };
  const rightStyle = { width: '80%', padding: '16px' };

  if (loading) {
    return (
      <Container fluid style={{ minHeight: '100vh', padding: '24px', textAlign: 'center' }}>
        <Spinner animation="border" role="status" />
        <p style={{ marginTop: 12 }}>Loading template data...</p>
      </Container>
    );
  }

  return (
    <Container fluid style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '24px' }}>
      {/* Heading Section */}
      <Row style={{ marginBottom: '16px' }}>
        <h3 style={{ fontWeight: 500, margin: 0 }}>
          Exchange Questions &gt; {isCloning ? 'Clone Template' : isEditMode ? 'Edit Template' : 'Create New Template'}
        </h3>
      </Row>

      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton style={{ border: '0px' }}>
          <Modal.Title>
            {isEditMode ? 'Confirm Update' : isCloning ? 'Confirm Clone' : 'Confirm Creation'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center', padding: '32px 24px' }}>
          {isEditMode
            ? 'This template will be updated with the provided details. Are you sure you want to proceed?'
            : isCloning
            ? 'A new cloned template will be created. Are you sure you want to proceed?'
            : 'This template will be created and assigned. Are you sure you want to proceed?'}
        </Modal.Body>
        <Modal.Footer style={{ border: '0px' }}>
          <Button variant="light" onClick={() => setShowConfirm(false)} style={{ width: '200px', border: '0px' }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveTemplate} style={{ width: '200px', border: '0px' }}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Template Details Section */}
      <Row style={{ marginBottom: '16px', paddingInline: '5px', paddingBlock: '16px', borderRadius: '4px', backgroundColor: '#ffffff' }}>
        <h6 style={{ fontWeight: 500, margin: 0 }}>Fill in the Details</h6>
      </Row>
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={leftStyle}>Template Details</div>
        <div style={rightStyle}>
          <Form.Group style={{ marginBottom: '12px' }}>
            <Form.Label>Template Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Example: 'Laptop Exchange Template'"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              style={{ backgroundColor: '#f8f9fa', borderRadius: '0px' }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Example: 'This template is used for laptop-related exchange questions.'"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ backgroundColor: '#f8f9fa', borderRadius: '0px' }}
            />
          </Form.Group>
        </div>
      </Row>

      {/* Select Questions Section */}
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={leftStyle}>Select Questions</div>
        <div style={rightStyle}>
          {selectedQuestions.map((sel, index) => (
            <InputGroup className="mb-2" key={index}>
              <EditableDropDown
                id={`question-${index}`}
                isMultiSelect={false}
                value={
                  sel
                    ? [{ value: sel, label: availableQuestions.find((q) => q.objectId === sel)?.text || sel }]
                    : []
                }
                options={questionOptions}
                onChange={(selectedOption) => handleQuestionChange(index, selectedOption)}
                style={{ height: 38, width: '100%' }}
                placeholder="Select a question"
              />
              <Button variant="light" onClick={() => handleRemoveQuestion(index)} style={{ border: '0px' }}>
                <X size={16} />
              </Button>
            </InputGroup>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="light" onClick={handleAddQuestion} style={{ border: '0px' }}>
              <Plus size={16} className="me-1" /> Add a Question
            </Button>
          </div>
        </div>
      </Row>

      {/* Dynamic Exchange Configurations Section */}
      {showExchangeConfig && (
        <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
          <div style={leftStyle}>Exchange Configurations</div>
          <div style={rightStyle}>
            <Row className="mb-3" style={{ fontWeight: 600 }}>
              {activeQuestions.map((_, idx) => (
                <Col key={idx}>{`Q${idx + 1}`}</Col>
              ))}
              <Col>Exchange Value</Col>
              <Col>Exchange Bonus</Col>
              <Col>BuyBack Sku</Col>
              <Col xs="auto" />
            </Row>
            {exchangeConfigRows.map((row, rowIndex) => (
              <Row
                key={rowIndex}
                className="mb-3"
                style={{ padding: '8px', border: '1px solid #dee2e6', marginBottom: '16px' }}
              >
                {activeQuestions.map((qId, qIndex) => (
                  <Col key={qIndex}>
                    <EditableDropDown
                      id={`answer-${rowIndex}-${qId}`}
                      isMultiSelect={false}
                      value={
                        row.answers && row.answers[qId]
                          ? [{ value: row.answers[qId], label: row.answers[qId] }]
                          : []
                      }
                      options={[
                        { label: 'Assigned', value: 'Assigned' },
                        ...((availableQuestions.find((q) => q.objectId === qId)?.answerOptions || [])
                          .map((opt) => ({ value: opt, label: opt })))
                      ]}
                      onChange={(selectedOption) => {
                        const selected =
                          selectedOption && selectedOption.length > 0
                            ? selectedOption[0].value
                            : '';
                        const updatedRows = [...exchangeConfigRows];
                        const currentAnswers = updatedRows[rowIndex].answers || {};
                        currentAnswers[qId] = selected;
                        updatedRows[rowIndex].answers = currentAnswers;
                        setExchangeConfigRows(updatedRows);
                      }}
                      style={{ height: 38, width: '100%' }}
                      placeholder="Select"
                    />
                  </Col>
                ))}
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Value"
                    value={row.exchangeValue}
                    onChange={(e) => updateExchangeConfigRow(rowIndex, 'exchangeValue', e.target.value)}
                    style={{ backgroundColor: '#f8f9fa', borderRadius: '0px' }}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Bonus"
                    value={row.exchangeBonus}
                    onChange={(e) => updateExchangeConfigRow(rowIndex, 'exchangeBonus', e.target.value)}
                    style={{ backgroundColor: '#f8f9fa', borderRadius: '0px' }}
                  />
                </Col>
                <Col>
                  <EditableDropDown
                    id={`buyBackSku-${rowIndex}`}
                    isMultiSelect={false}
                    value={
                      row.buyBackSku
                        ? [
                            {
                              value: row.buyBackSku,
                              label:
                                (availableBuyBackSkus.find((s) => s.id === row.buyBackSku)?.name) ||
                                row.buyBackSku
                            }
                          ]
                        : []
                    }
                    options={buyBackSkuOptions}
                    onChange={(selectedOption) =>
                      updateExchangeConfigRow(
                        rowIndex,
                        'buyBackSku',
                        selectedOption && selectedOption.length > 0 ? selectedOption[0].value : ''
                      )
                    }
                    style={{ height: 38, width: '100%' }}
                    placeholder="Select BuyBack Sku"
                  />
                </Col>
                <Col xs="auto">
                  {exchangeConfigRows.length > 1 && (
                    <Button
                      variant="light"
                      onClick={() =>
                        setExchangeConfigRows(exchangeConfigRows.filter((_, i) => i !== rowIndex))
                      }
                      style={{ marginTop: '4px' }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
            <Button
              variant="light"
              onClick={() =>
                setExchangeConfigRows([
                  ...exchangeConfigRows,
                  { answers: {}, exchangeValue: '', exchangeBonus: '', buyBackSku: '' }
                ])
              }
              style={{ border: '0px', width: '300px' }}
            >
              <Plus size={16} className="me-1" /> Add More
            </Button>
          </div>
        </Row>
      )}

      {/* Assignment Details Section */}
      {!isEditMode && (
        <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
          <div style={leftStyle}>Assignment Details</div>
          <div style={rightStyle}>
            <Form.Group style={{ marginBottom: '12px' }}>
              <EditableDropDown
                id="product"
                isMultiSelect={false}
                value={
                  selectedProduct
                    ? [
                        {
                          value: selectedProduct,
                          label:
                            (availableProducts.find((p) => p.id === selectedProduct)?.name) ||
                            selectedProduct
                        }
                      ]
                    : []
                }
                options={productOptions}
                onChange={(selectedOption) =>
                  setSelectedProduct(
                    selectedOption && selectedOption.length > 0 ? selectedOption[0].value : ''
                  )
                }
                style={{ height: 38, width: '100%' }}
                placeholder="Select a product"
              />
            </Form.Group>
            <Form.Group style={{ marginBottom: '12px' }}>
              <EditableDropDown
                id="brand"
                isMultiSelect={false}
                value={
                  selectedBrand
                    ? [
                        {
                          value: selectedBrand,
                          label:
                            (availableBrands.find((b) => b.id === selectedBrand)?.name) ||
                            selectedBrand
                        }
                      ]
                    : []
                }
                options={brandOptions}
                onChange={(selectedOption) =>
                  setSelectedBrand(
                    selectedOption && selectedOption.length > 0 ? selectedOption[0].value : ''
                  )
                }
                style={{ height: 38, width: '100%' }}
                placeholder="Select a brand"
              />
            </Form.Group>
            <Form.Group style={{ marginBottom: '12px' }}>
              <EditableDropDown
                id="company"
                isMultiSelect={false}
                value={
                  selectedCompany
                    ? [
                        {
                          value: selectedCompany,
                          label:
                            (availableCompanies.find((c) => c.id === selectedCompany)?.name) ||
                            selectedCompany
                        }
                      ]
                    : []
                }
                options={companyOptions}
                onChange={(selectedOption) =>
                  setSelectedCompany(
                    selectedOption && selectedOption.length > 0 ? selectedOption[0].value : ''
                  )
                }
                style={{ height: 38, width: '100%' }}
                placeholder="Select a company"
              />
            </Form.Group>
          </div>
        </Row>
      )}

      {/* Assigned Products Section */}
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={leftStyle}>Assigned To</div>
        <div style={rightStyle}>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Label>Products</Form.Label>
            {assignedProducts.length === 0 ? (
              <p style={{ color: '#6c757d' }}>Not Selected</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {assignedProducts.map((product, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: '#e9ecef',
                      border: '1px solid #ced4da',
                      borderRadius: '0px',
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <span>{product}</span>
                    <Button
                      variant="link"
                      onClick={() =>
                        setAssignedProducts(assignedProducts.filter((_, i) => i !== index))
                      }
                      style={{ marginLeft: '4px', padding: '0', color: 'red', fontSize: '14px' }}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Form.Group>
        </div>
      </Row>

      {/* Bottom Save/Cancel Buttons */}
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', width: '100%' }}>
          <Button variant="light" onClick={handleCancel} style={{ width: '200px', border: '0px' }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} style={{ width: '200px', border: '0px' }}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </div>
      </Row>
    </Container>
  );
};

export default CreateNewTemplate;
